/**
 * 画面共通の部品。モックの見た目（カード・バッジ・注記・表）に合わせている。
 */

import type { ReactNode } from 'react';
import type { IssueView, ReportStatus } from '../api/types.js';

export function Card({
  title,
  description,
  action,
  step,
  children,
  className = '',
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /** 入力の手順がある画面で節の順序を示す */
  step?: number;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <section className={`card ${className}`}>
      {(title ?? action) !== undefined && (
        <div className="card-head">
          <div>
            {title !== undefined && (
              <h2 className="section-title flex items-center gap-2">
                {step !== undefined && <span className="step-mark">{step}</span>}
                {title}
              </h2>
            )}
            {description !== undefined && (
              <p className="text-[0.8rem] text-ink-muted mt-0.5">{description}</p>
            )}
          </div>
          {action}
        </div>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

const STATUS_LABEL: Record<ReportStatus | 'empty' | 'locked', { label: string; cls: string }> = {
  empty: { label: '未入力', cls: 'pill--muted' },
  draft: { label: '下書き', cls: 'pill--muted' },
  submitted: { label: '提出済み', cls: 'pill--info' },
  approved: { label: '承認済み', cls: 'pill--ok' },
  rejected: { label: '差戻し', cls: 'pill--warn' },
  locked: { label: '確定', cls: 'pill--ok' },
};

export function StatusPill({
  status,
  locked = false,
}: {
  status: ReportStatus | null;
  locked?: boolean;
}): React.JSX.Element {
  const key = locked ? 'locked' : (status ?? 'empty');
  const { label, cls } = STATUS_LABEL[key];
  return <span className={`pill ${cls}`}>{label}</span>;
}

/** 暫定ルールを含む結果につける「試算」バッジ（画面仕様書 §2.2） */
export function ProvisionalBadge(): React.JSX.Element {
  return (
    <span className="pill pill--warn" title="制度未確定の項目を含むため試算表示です">
      試算
    </span>
  );
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  title?: string;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <div>
        {title !== undefined && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}

/** サーバーの警告（IssueView）とブラウザ側の検証結果（ValidationIssue）の共通部分 */
type DisplayableIssue = Pick<IssueView, 'severity' | 'code' | 'message'>;

export function IssueList({
  issues,
}: {
  issues: readonly DisplayableIssue[];
}): React.JSX.Element | null {
  if (issues.length === 0) return null;
  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  return (
    <div className="space-y-2">
      {errors.length > 0 && (
        <Notice tone="danger" title={`入力エラー ${errors.length}件`}>
          <ul className="list-disc pl-5">
            {errors.map((issue) => (
              <li key={issue.code + issue.message}>{issue.message}</li>
            ))}
          </ul>
        </Notice>
      )}
      {warnings.length > 0 && (
        <Notice tone="warn" title={`確認事項 ${warnings.length}件`}>
          <ul className="list-disc pl-5">
            {warnings.map((issue) => (
              <li key={issue.code + issue.message}>{issue.message}</li>
            ))}
          </ul>
        </Notice>
      )}
    </div>
  );
}

/**
 * 自動計算の結果（画面仕様書 §5.4）。
 * 入力欄と混同されないよう、色面の上に置いて編集できないことを示す。
 */
export function CalcPanel({
  title,
  note,
  provisional = false,
  main,
  rows,
  footer,
  children,
}: {
  title: string;
  note?: ReactNode;
  provisional?: boolean;
  main: { label: string; value: string }[];
  rows: { label: string; value: ReactNode }[];
  footer?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <section className="calc-panel space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[0.95rem] font-bold">{title}</h2>
        {provisional && <ProvisionalBadge />}
      </div>
      {note !== undefined && <p className="calc-note">{note}</p>}

      <div className="calc-main">
        {main.map((item) => (
          <div className="calc-main-item" key={item.label}>
            <span className="calc-main-label">{item.label}</span>
            <span className="calc-main-value tabular">{item.value}</span>
          </div>
        ))}
      </div>

      <dl>
        {rows.map((row) => (
          <div className="calc-row" key={row.label}>
            <dt>{row.label}</dt>
            <dd className="calc-row-value tabular">{row.value}</dd>
          </div>
        ))}
      </dl>

      {children}
      {footer !== undefined && <p className="calc-note">{footer}</p>}
    </section>
  );
}

export function Kpi({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="kpi">
      <p className="kpi-label">{label}</p>
      <p className="kpi-value">{value}</p>
      {note !== undefined && <p className="text-[0.78rem] text-ink-muted">{note}</p>}
    </div>
  );
}

export function EmptyState({ message }: { message: string }): React.JSX.Element {
  return <p className="py-8 text-center text-sm text-ink-muted">{message}</p>;
}

export function Loading(): React.JSX.Element {
  return <p className="py-10 text-center text-sm text-ink-muted">読み込んでいます…</p>;
}

export function ErrorMessage({ error }: { error: unknown }): React.JSX.Element | null {
  if (!error) return null;
  return <Notice tone="danger">{error instanceof Error ? error.message : String(error)}</Notice>;
}

/** 項目名と値を縦に並べる定義リスト */
export function DataList({
  items,
  columns = 3,
}: {
  items: { label: string; value: ReactNode }[];
  columns?: 2 | 3 | 4;
}): React.JSX.Element {
  const cls = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[columns];
  return (
    <dl className={`grid grid-cols-2 gap-x-4 gap-y-3 ${cls}`}>
      {items.map((item) => (
        <div key={item.label}>
          <dt className="text-[0.78rem] text-ink-muted">{item.label}</dt>
          <dd className="text-[0.95rem] font-semibold">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
